import React, { useState, useContext } from 'react';
import { IndexContext } from '../../context';
import SocialButtons from '../../../auth-shared/SocialButtons.js';
import { getEnabledProviders, readOAuthClientIds } from '../../../auth-shared/enabledProviders.js';
import { DASHBOARD_URL } from '../../../auth-shared/completeOAuthSignIn.js';
import { useSocialSignIn } from '../../../auth-shared/useSocialSignIn.js';
import { parseLoginError } from '../../../login/parseLoginError.js';

const Hero = ({ request, heroSection, styles = {} }) => {
  const { baseUrl, isLogged } = useContext(IndexContext);

  const [state, setState] = useState({
    email: request.email || '',
    password: request.password || ''
  });
  const [oauthError, setOauthError] = useState(null);
  const clientIds = readOAuthClientIds();
  const providers = getEnabledProviders(clientIds);
  const social = useSocialSignIn({
    clientIds,
    redirectTo: DASHBOARD_URL,
    onStart: () => setOauthError(null),
    // A closed popup or SDK failure has no backend response for parseLoginError to read.
    onError: err =>
      setOauthError(err?.response ? parseLoginError(err).message : err?.message || 'Sign-in failed. Please try again.')
  });
  // 'microsoft' locks nothing: its popup cannot be watched, so the visitor may retry or switch at any time.
  const oauthBusy = social.phase === 'google' || social.phase === 'exchanging';
  const handleSubmit = e => {
    if (social.isExchanging()) {
      e.preventDefault();
      return;
    }
    social.cancelMicrosoftWait();
  };

  return (
    <header className={`index-header ${styles.container || ''}`}>
      <picture>
        <source media="(min-width: 769px)" srcSet={heroSection.airplaneBg} type="image/png" />
        <source media="(max-width: 768px)" srcSet="data:image/gif;base64,01" type="image/gif" />
        <img
          className="index-header-bg"
          data-src={heroSection.airplaneBg}
          alt={heroSection.planeAlt}
        />
      </picture>
      <div className={`container-block index-header-inner ${styles.headerInner || ''}`}>
        <div className="index-header-left-column">
          <figure className="index-logo-img-phone-figure">
            <a href="#">
              <img
                src={heroSection.logoImg}
                alt={heroSection.logoAlt}
                title={heroSection.logoAlt}
                className="index-logo-img-phone lazyload"
                loading="lazy"
              />
            </a>
          </figure>
          <div className="index-header-inner-info">
            <h1 className="index-white-heading" children={heroSection.heading} />
            <p className="index-header-text" children={heroSection.headerText} />
            <div className={`app-links-horizontal ${styles.app}`}>
              <a
                className="app-link"
                href={heroSection.appleStoreLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <picture>
                  <source srcSet={heroSection.appleStoreSrc} type="image/png" />
                  <img
                    src={heroSection.appleStoreSrc}
                    alt={heroSection.appStoreTitle}
                    title={heroSection.appStoreTitle}
                    height="57px"
                    width="170px"
                  />
                </picture>
              </a>
              <a
                className="app-link"
                href={heroSection.playMarketLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <picture>
                  <source srcSet={heroSection.playMarketSrc} type="image/png" />
                  <img
                    className="app-link-img-android"
                    src={heroSection.playMarketSrc}
                    alt={heroSection.playMarketTitle}
                    title={heroSection.playMarketTitle}
                    height="64px"
                    width="166px"
                  />
                </picture>
              </a>
            </div>
          </div>
        </div>
        {!isLogged && (
          <div className="index-header-right-column">
            <div className="form-container">
              <div className={`index-header-sing-in ${styles.form}`}>
                <div className="index-header-sing-in-heading">{heroSection.signInHeading}</div>
                <form
                  className="index-header-sing-in-form js--index-header-sing-in-form"
                  role="form"
                  method="post"
                  action={`${baseUrl}/authorization/login/`}
                  onSubmit={handleSubmit}
                >
                  <div className="input-group">
                    <input
                      name="email"
                      id="email"
                      maxLength="50"
                      autoComplete="username"
                      className="input-field"
                      value={state.email}
                      onChange={e => {
                        setState({
                          ...state,
                          email: e.target.value
                        });
                      }}
                      required
                      placeholder="Email"
                      type="email"
                    />
                  </div>
                  <div className="index-header-sing-in-form-group">
                    <div className="input-group">
                      <input
                        name="password"
                        id="password"
                        maxLength="50"
                        autoComplete="current-password"
                        className="input-field"
                        value={state.password}
                        onChange={e => {
                          setState({
                            ...state,
                            password: e.target.value
                          });
                        }}
                        required
                        placeholder="Password"
                        type="password"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="sign-in-button"
                    disabled={oauthBusy}
                    children={heroSection.formSubmit.signInText}
                  />
                  {oauthError && (
                    <p className="social-error" role="alert">
                      {oauthError}
                    </p>
                  )}
                  <SocialButtons
                    providers={providers}
                    onGoogle={social.signInGoogle}
                    onMicrosoft={social.signInMicrosoft}
                    isGoogleDisabled={social.phase === 'exchanging'}
                    isMicrosoftDisabled={oauthBusy}
                    showMicrosoftHint={social.phase === 'microsoft'}
                    radius={6}
                  />
                </form>
                <div className="index-header-sing-in-subform">
                  <a
                    className="index-header-sing-in-subform-control"
                    href="/authorization/registration/"
                    children={heroSection.formSubmit.signUpText}
                  />
                  <a
                    className="index-header-sing-in-subform-control"
                    href={heroSection.formSubmit.forgotPassLink}
                  >
                    {heroSection.formSubmit.forgotPass}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Hero;
